<script setup lang="ts">
import InputHelperText from "frontcore/views/select/InputHelperText.vue"
import SVGIcon from 'frontcore/views/icons/SVGIcon.vue'
import TEXT_FIELD_LENGTHS from 'frontcore/constants/textFieldLengths'
import useInputState from 'frontcore/views/input-text/useInputState'
import { reactive, ref, toRefs, watch } from 'vue'

const props = withDefaults(defineProps<{
  dataTest?: string
  disabled?: boolean
  error?: string
  iconClass?: string
  iconName?: string
  iconNameLeft?: string
  label?: string
  maxLength?: number,
  placeholder?: string
  supportingText?: string
  type?: string
  value?: string | number | null
}>(), {
  disabled: false,
  error: '',
  label: '',
  maxLength: TEXT_FIELD_LENGTHS.LARGE_TEXT_FIELDS,
  placeholder: '',
  supportingText: '',
  type: 'text',
  value: '',
})

const { dataTest, disabled, error, iconName, value } = toRefs(props)

const initialValue = ref(props.value)

type Emits = {
  (e: 'input', value: string):  void
  (e: 'clear'): void
}
const emit = defineEmits<Emits>()

const emitValue = (e:Event) => {
  const newValue = (e.target as HTMLInputElement).value
  initialValue.value = newValue
  emit('input', newValue)
}

const clearValue = () => {
  initialValue.value = ''
  emit('input', '')
}

const {
  stateStyleClass,
} = useInputState(reactive({ disabled, error, value }))

watch(
  () => props.value,
  (newValue) => {
    initialValue.value = newValue
  },
  { immediate: true }
)

const isInputFocus = ref(false)
const isMouseEnter = ref(false)

const handleFocus = (isFocus: boolean) => {
  isInputFocus.value = isFocus
}

const handleMouse = (isMouse : boolean) => {
  isMouseEnter.value = isMouse
}
</script>
<template>
  <div
    class="input-text__container"
    :class="[stateStyleClass, {'pointer': !disabled, 'input-text__state-disabled': disabled}]">
    <span
      v-if="label.length >0"
      class="input-text__label tw-block body--auxiliary-01-bold">{{ label }}
    </span>
    <div
      class="input-text__input-container"
      :data-test="`${dataTest}--input-wrapper`"
      @mouseenter="handleMouse(true)"
      @mouseleave="handleMouse(false)">
      <div
        class="flex align-center gap-2 tw-w-full">
        <SVGIcon
          v-if="iconNameLeft"
          class="input-text__left-icon"
          :iconName="iconNameLeft" />
        <input
          ref="input"
          :value="initialValue"
          :type="type"
          :data-test="dataTest"
          :disabled="disabled"
          :placeholder="placeholder"
          :maxlength="maxLength"
          class="input-text__input"
          @input="emitValue"
          @focus="handleFocus(true)"
          @blur="handleFocus(false)">
        <SVGIcon
          v-if="error"
          colorClass="fill-auxiliary-error-30"
          iconName="Error" />
        <SVGIcon
          v-if="error || isInputFocus || isMouseEnter"
          colorClass="fill-core-neutral-cold-20"
          iconName="Close"
          :remSize="1"
          @click="clearValue" />
        <SVGIcon
          v-if="iconName"
          :colorClass="iconClass"
          :iconName="iconName" />
      </div>
    </div>
    <div class="flex w-full">
      <slot name="supporting-text">
        <InputHelperText
          :data-test="`${dataTest}--supporting-text`"
          :error="error"
          :supportingText="supportingText" />
      </slot>
      <slot name="supporting-text-right-content" />
    </div>
  </div>
</template>

<style scoped>
input::placeholder {
  color: var(--core-neutral-cold-60);
}
</style>
